import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accessDBApi, handleApiError } from '../../../services/api';
import { showToast } from '../../../stores/uiStore';
import { getOperationErrorMessage } from '../../../config/errorMessages';
import { useSessionGate } from '../../../hooks/useSessionGate';
import { getDomainDiscoverPath } from '../../../config/routes';

interface UnansweredQuestionItem {
  question_text: string;
  rag_hit_count: number;
  max_similarity: number;
  first_seen: string;
  last_seen: string;
}

interface UnansweredQuestionsResponse {
  success: boolean;
  data: {
    items: UnansweredQuestionItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
}

interface Props {
  domainId: string;
}

type SortOrder = 'count' | 'date';


const periods = [
  { value: 7 as const, label: '7日間' },
  { value: 30 as const, label: '30日間' },
];

export const UnansweredQuestions: React.FC<Props> = ({ domainId }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<7 | 30>(30);
  const [sort, setSort] = useState<SortOrder>('count');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { expired } = useSessionGate();
  const { data, isLoading, error } = useQuery<UnansweredQuestionsResponse>({
    queryKey: ['unansweredQuestions', domainId, period, sort, page],
    queryFn: async () => {
      const response = await accessDBApi.get(
        `/v1/domains/${domainId}/unanswered-questions`,
        { params: { period, sort, page, limit } },
      );
      return response.data;
    },
    enabled: !!domainId && !expired,
  });

  useEffect(() => {
    if (error) {
      const apiError = handleApiError(error);
      showToast.error(apiError.message || getOperationErrorMessage('fetch'));
    }
  }, [error]);

  const items = data?.data?.items ?? [];
  const pagination = data?.data?.pagination;

  const handlePeriodChange = useCallback((value: 7 | 30) => {
    setPeriod(value);
    setPage(1);
  }, []);

  const handleSortToggle = useCallback((newSort: SortOrder) => {
    setSort(newSort);
    setPage(1);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      {/* ヘッダー + 期間フィルタ */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <span className="text-sm font-semibold text-text-primary">未回答の質問</span>
          <span className="ml-2 text-xs text-text-secondary">AIが答えられなかった質問、学習に追加できます。</span>
        </div>
        <div
          className="inline-flex rounded-md border border-input overflow-hidden"
          data-testid="unanswered-period-filter"
        >
          {periods.map((p, i) => (
            <button
              key={p.value}
              type="button"
              onClick={() => handlePeriodChange(p.value)}
              className={[
                'px-3 py-1.5 text-sm transition-colors',
                i > 0 ? 'border-l border-input' : '',
                period === p.value
                  ? 'bg-primary text-white'
                  : 'hover:bg-gray-100 text-text-primary',
              ].join(' ')}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ローディング */}
      {isLoading && (
        <div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse bg-gray-200 rounded h-12 mb-2" />
          ))}
        </div>
      )}

      {/* 空状態 */}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-12" data-testid="unanswered-empty-state">
          <p className="text-text-secondary">
            未回答の質問はありません。FAQの充実度が高い状態です。
          </p>
        </div>
      )}

      {/* テーブル */}
      {!isLoading && items.length > 0 && (
        <>
          <div className="overflow-x-auto" data-testid="unanswered-questions-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-2 text-left font-medium">質問内容</th>
                  <th className="px-4 py-2 text-right font-medium whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleSortToggle('count')}
                      className="inline-flex items-center gap-1 font-medium hover:text-primary"
                    >
                      回数{sort === 'count' ? ' ▼' : ''}
                    </button>
                  </th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50" data-testid="unanswered-question-row">
                    <td className="px-4 py-2">{item.question_text}</td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: '#F97316' }}
                      >
                        {item.rag_hit_count}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(getDomainDiscoverPath(domainId))}
                        className="text-xs font-medium text-primary border border-primary rounded px-2 py-1 hover:bg-primary/10 whitespace-nowrap"
                        data-testid="unanswered-add-answer-button"
                      >
                        回答を追加
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-input rounded disabled:opacity-50 hover:bg-gray-100 disabled:cursor-not-allowed"
              >
                前へ
              </button>
              <span className="text-sm text-text-secondary">
                ページ {page} / {pagination.total_pages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={page === pagination.total_pages}
                className="px-3 py-1 text-sm border border-input rounded disabled:opacity-50 hover:bg-gray-100 disabled:cursor-not-allowed"
              >
                次へ
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
